import type { ReactNode } from "react";
import { EmptyState } from "@/shared/ui/emptyState/public";
import {
  CompareTable,
  type CompareTableCell,
  type CompareTableColumn,
  type CompareTableRow,
} from "./CompareTable";
import type { EvalMergedResult, SeedStabilityRow } from "./evalCompareMergeModel";
import tableStyles from "./CompareTable.module.css";
import styles from "./EvaluationComparisonViewer.module.css";

const COLUMNS: readonly CompareTableColumn[] = ["Mean", "SD", "Min", "Max", "Seeds"].map(
  (header) => ({ key: header.toLowerCase(), header }),
);

function valueCell(value: number | null, format: (value: number) => string): CompareTableCell {
  return {
    value: value === null ? null : format(value),
    missingLabel: "N/A",
  };
}

function stabilityRow(row: SeedStabilityRow): CompareTableRow {
  return {
    key: row.key,
    group: row.group,
    groupKey: row.group,
    label: row.statistic,
    cells: [
      valueCell(row.stats.mean, row.format),
      valueCell(row.stats.sampleSd, row.formatSpread),
      valueCell(row.stats.min, row.format),
      valueCell(row.stats.max, row.format),
      { value: String(row.stats.count) },
    ],
  };
}

function episodesPerSeedLabel(result: EvalMergedResult): string {
  const range = result.episodesPerSeed;
  if (range === null) return "N/A";
  return range.min === range.max ? String(range.min) : `${range.min} – ${range.max}`;
}

export function SeedStabilityTable({ result }: { result: EvalMergedResult }): ReactNode {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Seed 안정성</h3>

      <dl className={styles.seedFacts}>
        <div className={styles.seedFact}>
          <dt>고유 Seed</dt>
          <dd>{result.uniqueSeedCount}</dd>
        </div>
        <div className={styles.seedFact}>
          <dt>포함된 실행</dt>
          <dd>{result.includedRunCount}</dd>
        </div>
        <div className={styles.seedFact}>
          <dt>Seed 당 Episode</dt>
          <dd>{episodesPerSeedLabel(result)}</dd>
        </div>
      </dl>

      {result.uniqueSeedCount > 0 && result.uniqueSeedCount <= 3 ? (
        <p className={styles.infoNotice} role="status">
          신뢰도가 제한적입니다: 고유 Simulation Seed 가 {result.uniqueSeedCount}개뿐이라 기술
          통계로만 읽어 주세요.
        </p>
      ) : null}

      {result.stabilityRows.length > 0 ? (
        <CompareTable
          groupHeader="지표"
          leafHeader="통계"
          columns={COLUMNS}
          rows={result.stabilityRows.map(stabilityRow)}
          footer={
            <p className={tableStyles.footnote}>
              통계는 고유 Simulation Seed 당 값 하나로 계산합니다.
            </p>
          }
        />
      ) : (
        <EmptyState size="sm">표시할 Seed 통계가 없습니다.</EmptyState>
      )}
    </section>
  );
}
